import React, { useEffect, useMemo } from 'react';
import './LegalPage.css';

const SIGNUP_SOURCES = new Set(['navbar', 'referral', 'feed']);

const LegalPage = () => {
  const { fromSignup, homeHref, backLabel } = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const fromSignup = params.get('from') === 'signup';
    const rawSrc = params.get('signupSource') || '';
    const signupSource = SIGNUP_SOURCES.has(rawSrc) ? rawSrc : '';
    if (!fromSignup) {
      return { fromSignup: false, homeHref: '/', backLabel: '← Back to collages' };
    }
    const q = new URLSearchParams({ openSignup: '1' });
    if (signupSource) q.set('signupSource', signupSource);
    return {
      fromSignup: true,
      homeHref: `/?${q.toString()}`,
      backLabel: '← Back to sign up',
    };
  }, []);

  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace(/^#/, '');
      if (!id) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };

    scrollToHash();
    const t = window.setTimeout(scrollToHash, 50);
    window.addEventListener('hashchange', scrollToHash);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('hashchange', scrollToHash);
    };
  }, []);

  return (
  <div className="legal-page">
    <header className="legal-page-header">
      <a href={homeHref} className="legal-page-back">
        {backLabel}
      </a>
    </header>
    <main className="legal-page-main">
      <h1 className="legal-page-title">Legal Terms</h1>
      <p className="legal-page-updated">Last Updated: Apr-2026</p>

      <section className="legal-section" id="legal-terms">
        <h2 className="legal-h2">1. Terms of Service</h2>
        <p>
          These Terms of Service (&ldquo;Terms&rdquo;) govern your use of our web application and related
          services (&ldquo;Service&rdquo;). By creating an account or using the Service, you agree to these
          Terms.
        </p>

        <h3 className="legal-h3">Eligibility</h3>
        <ul>
          <li>You must be at least 18 years old to use the Service.</li>
          <li>By using the Service, you confirm you have the legal capacity to enter into these Terms.</li>
        </ul>

        <h3 className="legal-h3">Account Registration</h3>
        <ul>
          <li>You must provide accurate and current information when creating an account.</li>
          <li>
            You are responsible for maintaining the confidentiality of your login credentials and for all
            activity under your account.
          </li>
          <li>You may not impersonate others or create accounts on their behalf.</li>
        </ul>

        <h3 className="legal-h3">User Content</h3>
        <ul>
          <li>You may upload a profile photo and choose a nickname.</li>
          <li>You retain ownership of your content.</li>
          <li>
            You grant us permission to host, store, display, and distribute your content solely to operate and
            improve the Service.
          </li>
          <li>You represent that you have the rights to upload any content you submit.</li>
        </ul>

        <h3 className="legal-h3">Prohibited Conduct</h3>
        <p>You agree not to:</p>
        <ul>
          <li>Harass, threaten, or harm other users</li>
          <li>Upload illegal, hateful, discriminatory, or sexually explicit content</li>
          <li>Use the Service for spam, solicitation, or commercial purposes</li>
          <li>Scrape, reverse-engineer, or disrupt the Service</li>
          <li>Misrepresent your identity or age</li>
          <li>Upload photos of others without their permission</li>
        </ul>

        <h3 className="legal-h3">Community Guidelines</h3>
        <p>To maintain a respectful environment, users must:</p>
        <ul>
          <li>Communicate honestly and respectfully</li>
          <li>Use only their own photo and nickname</li>
          <li>Avoid abusive, manipulative, or deceptive behavior</li>
          <li>Report harmful or suspicious activity</li>
        </ul>
        <p>Violations may result in warnings, content removal, or account suspension.</p>

        <h3 className="legal-h3">Content Moderation</h3>
        <p>
          We may review, remove, or restrict access to content or accounts that violate these Terms or our
          policies. Moderation actions may include:
        </p>
        <ul>
          <li>Removing photos or nicknames</li>
          <li>Temporarily restricting account features</li>
          <li>Permanently banning accounts</li>
        </ul>
        <p>
          We may use automated tools and human review. Moderation decisions are final.
        </p>

        <h3 className="legal-h3">Intellectual Property</h3>
        <p>
          All trademarks, logos, and software associated with the Service belong to us or our licensors. You
          may not copy, modify, or distribute any part of the Service without written permission.
        </p>

        <h3 className="legal-h3">Termination</h3>
        <p>
          We may suspend or terminate your account at any time for violating these Terms or for behavior that
          may harm the Service or its users. You may delete your account by emailing{' '}
          <a href="mailto:contact@luv.wtf">contact@luv.wtf</a> at any time.
        </p>

        <h3 className="legal-h3">Limitation of Liability</h3>
        <p>
          To the fullest extent permitted by law, we are not liable for any indirect, incidental, or
          consequential damages arising from your use of the Service.
        </p>

        <h3 className="legal-h3">Dispute Resolution</h3>
        <p>
          Any disputes arising under these Terms will be resolved through binding arbitration on an individual
          basis. You waive any right to participate in class-action proceedings.
        </p>

        <h3 className="legal-h3">Governing Law</h3>
        <p>
          These Terms are governed by the laws of the State of California, without regard to conflict-of-law
          principles.
        </p>
      </section>

      <section className="legal-section" id="legal-privacy">
        <h2 className="legal-h2">2. Privacy Policy</h2>
        <p>
          This Privacy Policy explains how we collect, use, and share your information when you use the
          Service.
        </p>

        <h3 className="legal-h3">Information We Collect</h3>
        <p>We collect:</p>
        <ul>
          <li>Profile information: nickname and profile photo</li>
          <li>Account information: email, phone number (optional), or login credentials</li>
          <li>Usage data: interactions within the web app</li>
          <li>Optional information: anything you choose to share in your profile or messages</li>
        </ul>
        <p>We do not collect sensitive categories of data.</p>

        <h3 className="legal-h3">How We Use Your Information</h3>
        <p>We use your information to:</p>
        <ul>
          <li>Operate and improve the Service</li>
          <li>Facilitate matching and communication features</li>
          <li>Maintain safety and enforce policies</li>
          <li>Analyze usage trends and performance</li>
          <li>Provide customer support</li>
        </ul>
        <p>We do not sell your personal information.</p>

        <h3 className="legal-h3">Sharing Your Information</h3>
        <p>We may share information with:</p>
        <ul>
          <li>Service providers (hosting, analytics, moderation)</li>
          <li>Law enforcement when required by law</li>
        </ul>
        <p>We do not share your profile photo or nickname with third parties for advertising.</p>

        <h3 className="legal-h3">Your Rights (California Residents)</h3>
        <p>Under the California Consumer Privacy Act (CCPA), you may:</p>
        <ul>
          <li>Request access to the personal information we hold</li>
          <li>Request deletion of your personal information</li>
          <li>Request correction of inaccurate information</li>
        </ul>
        <p>
          You may exercise these rights by emailing <a href="mailto:contact@luv.wtf">contact@luv.wtf</a> at
          any time.
        </p>

        <h3 className="legal-h3">Data Retention</h3>
        <p>
          We retain your information while your account is active. Deleted accounts are purged within a
          reasonable period unless retention is required by law.
        </p>

        <h3 className="legal-h3">Security</h3>
        <p>
          We use reasonable administrative, technical, and physical safeguards to protect your information.
        </p>

        <h3 className="legal-h3">Children&rsquo;s Privacy</h3>
        <p>
          The Service is not intended for individuals under 18. We do not knowingly collect information from
          minors.
        </p>
      </section>

      <section className="legal-section">
        <h2 className="legal-h2">3. Copyright &amp; DMCA Policy (California-Specific)</h2>
        <p>
          We respect intellectual property rights and comply with the Digital Millennium Copyright Act
          (&ldquo;DMCA&rdquo;) and applicable California laws.
        </p>

        <h3 className="legal-h3">Reporting Copyright Infringement</h3>
        <p>If you believe content on the Service infringes your copyright, you may submit a DMCA notice including:</p>
        <ul>
          <li>Your name, address, and contact information</li>
          <li>A description of the copyrighted work</li>
          <li>A description of the infringing material</li>
          <li>A statement under penalty of perjury that your claim is accurate</li>
          <li>A statement that you are the copyright owner or authorized agent</li>
          <li>Your physical or electronic signature</li>
        </ul>
        <p>We will respond to valid DMCA notices in accordance with federal law.</p>

        <h3 className="legal-h3">Removal of Content</h3>
        <p>Upon receiving a valid DMCA notice, we may:</p>
        <ul>
          <li>Remove or disable access to the allegedly infringing content</li>
          <li>Notify the user who uploaded it</li>
        </ul>
        <p>We may terminate accounts of repeat infringers.</p>

        <h3 className="legal-h3">Counter-Notification</h3>
        <p>If your content was removed in error, you may submit a counter-notification including:</p>
        <ul>
          <li>Your name, address, and contact information</li>
          <li>Identification of the removed content</li>
          <li>A statement under penalty of perjury that the removal was due to mistake or misidentification</li>
          <li>Your consent to jurisdiction in the federal courts of California</li>
          <li>Your physical or electronic signature</li>
        </ul>
        <p>If valid, we may restore the content unless the original complainant initiates legal action.</p>

        <h3 className="legal-h3">California Notice</h3>
        <p>California law requires us to inform users that:</p>
        <ul>
          <li>We do not provide legal advice regarding copyright disputes</li>
          <li>You may seek legal counsel before submitting a DMCA notice or counter-notice</li>
          <li>Knowingly submitting false claims may result in liability under federal law</li>
        </ul>
      </section>

      <p className="legal-page-footer-note">
        Questions?
        <a href="mailto:contact@luv.wtf" className="legal-page-footer-email">contact@luv.wtf</a>
      </p>
    </main>
  </div>
  );
};

export default LegalPage;
